import type { FaseStatus } from '@/utils/dashboardFases';

interface DashboardStatusLineProps {
  /** Derived status of the active fase (or the project roll-up in "Alles"). null hides the chip. */
  faseStatus: FaseStatus | null;
  /** Whether the project's contract is fully invoiced (from the vordering payload). */
  fullyInvoiced: boolean;
  /** Collectors whose uitvoering photos are uploaded but not yet approved. */
  fotosWachten: number;
}

const FASE_LABEL: Record<FaseStatus, { label: string; cls: string }> = {
  gepland: { label: 'Gepland', cls: 'bg-gray-200 text-td-navy' },
  in_uitvoering: { label: 'In uitvoering', cls: 'bg-td-blue/15 text-td-blue' },
  uitgevoerd: { label: 'Uitgevoerd', cls: 'bg-td-green/15 text-td-green' },
};

/**
 * One-line "needs attention" / status strip directly under the header. All three
 * signals are derived in-memory from already-loaded data — no fetch:
 *  - fase state badge (faseStatus over the active aggregate),
 *  - "Volledig gefactureerd" when the vordering payload reports fully_invoiced,
 *  - a magenta "N foto's wachten op goedkeuring" pill (collectors with
 *    x_studio_fotos_uitvoering === 'Fotos_Geupload'), hidden when N is 0.
 *
 * Renders nothing when there is nothing to say.
 */
export function DashboardStatusLine({ faseStatus, fullyInvoiced, fotosWachten }: DashboardStatusLineProps) {
  const fase = faseStatus ? FASE_LABEL[faseStatus] : null;
  if (!fase && !fullyInvoiced && fotosWachten <= 0) return null;

  return (
    <div aria-label="Projectstatus" className="flex flex-wrap items-center gap-2">
      {fase && (
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${fase.cls}`}>{fase.label}</span>
      )}
      {fullyInvoiced && (
        <span className="inline-flex items-center gap-1 rounded-full bg-td-green/15 px-2.5 py-0.5 text-xs font-bold text-td-green">
          <span aria-hidden="true">✓</span>
          Volledig gefactureerd
        </span>
      )}
      {fotosWachten > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-td-magenta/10 px-2.5 py-0.5 text-xs font-bold text-td-magenta">
          <span aria-hidden="true">📷</span>
          {fotosWachten} {fotosWachten === 1 ? "foto" : "foto's"} wachten op goedkeuring
        </span>
      )}
    </div>
  );
}
